import { Box, Button, CircularProgress, debounce } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPlacementMarginCancelTriggeredStatus,
  selectPlacementMarginOrderIdModifiedConfirmationStatus,
  selectPlacementMarginSaveTriggeredStatus,
  selectSelectedPlacementMarginScenario,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { useMarginToolFooterStyles } from './MarginToolFooter.Styles';
import { convertToPercentage } from '../PayPackage/helper';
import { PlacementMarginPayPackageStatus } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.model';
import { placementMarginScenarioActions } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { useFormContext } from 'react-hook-form';
import { usePromiseTracker } from 'react-promise-tracker';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import MarginToolConfirmationModal from './MarginToolConfirmationModal';
import { selectPlacementMarginTimeOffGridError } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.selector';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { IPayPackageCategory } from '@AMIEWEB/MarginTool/enum';
import { useFilterPayPackageValuesByCategoryId } from '../Assignments/helper';
import { getBonuses } from '../Assignments/Bonuses/helper';
import { getReimbursement } from '../Assignments/Reimbursement/helper';
import { cloneDeep } from 'lodash';
import { getMarginOverTimeHourlyRatesFromScenario } from '../PayPackage/AddScenario/helper';
import { usePeopleSoftCalculation } from '../PeopleSoftCalculation/IPeopleSoftCalculationHelper';
import { useReadOnly } from 'oidc/userRoles';

const MarginToolFooter = ({ initialMarginToolValues }) => {
  const { t } = useTranslation();
  const selectedScenario = useSelector(selectSelectedPlacementMarginScenario);
  const { classes } = useMarginToolFooterStyles();
  const isSaveTriggered = useSelector(selectPlacementMarginSaveTriggeredStatus);
  const isCancelTriggered = useSelector(selectPlacementMarginCancelTriggeredStatus);
  const reimbursementPayPackageValues = useFilterPayPackageValuesByCategoryId(IPayPackageCategory.Reimbursements);
  const bonusesPayPackageValues = useFilterPayPackageValuesByCategoryId(IPayPackageCategory.BonusType);
  const orderIdModifiedConfirmationStatus = useSelector(selectPlacementMarginOrderIdModifiedConfirmationStatus);
  const { readOnly } = useReadOnly();
  const { triggerPeopleSoftCalculation } = usePeopleSoftCalculation();

  const { promiseInProgress: flagVariationInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool,
    delay: 0,
  });
  const { promiseInProgress: isSavingScenario } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.saveScenario,
    delay: 0,
  });
  const { promiseInProgress: isLoadDataScenario } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.scenarioNavigationData,
    delay: 0,
  });

  const { promiseInProgress: isScenarioVerification } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.validateScenario,
    delay: 0,
  });

  const { promiseInProgress: isGSACalculateFetchDataInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData,
    delay: 0,
  });

  const { promiseInProgress: isPeopleSoftCallMarginToolInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress,
    delay: 0,
  });

  const { promiseInProgress: isCalculatedUpdateInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.updateScnarioCalculatedValues,
    delay: 0,
  });

  const editTimeOffGridError = useSelector(selectPlacementMarginTimeOffGridError);

  const [cancelModal, setCancelModalModal] = useState<boolean>(false);
  const dispatch = useDispatch();
  const {
    watch,
    reset,
    formState: { isDirty, errors },
  } = useFormContext();
  const hasErrors = Object.keys(errors).length > 0;
  const handleCancelButton = () => {
    setCancelModalModal(true);
  };
  const handleCancelModalClose = () => {
    setCancelModalModal(false);
    dispatch(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
  };
  const handleCancelModalYes = () => {
    const savedReimbursements = getReimbursement(reimbursementPayPackageValues);
    const bonuses = getBonuses(bonusesPayPackageValues);
    const newFormData = {
      payPackage: {
        placementId: selectedScenario?.placementId,
      },
      assignmentSplits: cloneDeep(selectedScenario?.splits) || [],
      assignment: {
        travel: selectedScenario?.assignment?.travel,
        timeOffs: selectedScenario?.assignment?.timeOffs,
        reimbursementFieldArray: cloneDeep(savedReimbursements) ?? [],
        bonusesFieldArray: cloneDeep(bonuses) ?? [],
      },
    };
    reset(newFormData);
    const marginOverTimeHourlyRates = getMarginOverTimeHourlyRatesFromScenario(selectedScenario);
    dispatch(placementMarginScenarioActions.setPlacementMarginOverTimeHourlyRates(marginOverTimeHourlyRates));
    dispatch(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
    dispatch(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    dispatch(globalActions.closeBanner());

    const triggerPeopleSoftCalculationTrigger = debounce(() => {
      triggerPeopleSoftCalculation(true);
    }, 1000);

    triggerPeopleSoftCalculationTrigger();
    setCancelModalModal(false);
  };
  const handleSaveButton = () => {
    const formData = watch();
    dispatch(
      placementMarginScenarioActions.postPlacementMarginToolDetails({
        formData,
        loadDetails: true,
      }),
    );
  };

  const isPendingAndDirty =
    selectedScenario?.scenarioStatusId === PlacementMarginPayPackageStatus.Pending && isDirty && !editTimeOffGridError;

  return (
    <>
      <Box className={classes.marginToolFooter}>
        <Box className={classes.marginToolFooterGrossMargin}>
          {t('marginTool.components.footer')}
          {flagVariationInProgress ||
          isScenarioVerification ||
          isGSACalculateFetchDataInProgress ||
          isPeopleSoftCallMarginToolInProgress ? (
            <Box ml={0.5}>
              <CircularProgress size={16} color="inherit" />
            </Box>
          ) : (
            ` ${convertToPercentage(selectedScenario?.grossMargin)}`
          )}
        </Box>
        <Box style={{ display: 'flex', gap: '16px' }}>
          <Button
            variant="contained"
            color="tertiary"
            className={classes.marginToolFooterButton}
            onClick={handleCancelButton}
            onMouseDown={() => {
              if (!isCancelTriggered) {
                if (orderIdModifiedConfirmationStatus) {
                  dispatch(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
                } else {
                  dispatch(placementMarginScenarioActions.setSavePlacementMarginTriggeredStatus(false));
                  dispatch(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(true)); // Set Save intent
                }
              }
            }}
            disabled={
              !isPendingAndDirty ||
              isSavingScenario ||
              flagVariationInProgress ||
              isLoadDataScenario ||
              isScenarioVerification ||
              isGSACalculateFetchDataInProgress ||
              isPeopleSoftCallMarginToolInProgress ||
              isCalculatedUpdateInProgress
            }
          >
            {t('marginTool.buttons.cancel')}
          </Button>
          <Button
            disabled={
              !isPendingAndDirty ||
              isSavingScenario ||
              flagVariationInProgress ||
              isLoadDataScenario ||
              hasErrors ||
              isScenarioVerification ||
              isGSACalculateFetchDataInProgress ||
              isPeopleSoftCallMarginToolInProgress ||
              isCalculatedUpdateInProgress ||
              readOnly
            }
            variant="contained"
            color={'primary'}
            className={classes.marginToolFooterButton}
            onMouseDown={() => {
              if (!isSaveTriggered) {
                if (orderIdModifiedConfirmationStatus) {
                  dispatch(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
                } else {
                  dispatch(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
                  dispatch(placementMarginScenarioActions.setSavePlacementMarginTriggeredStatus(true)); // Set Save intent
                }
              }
            }}
            onClick={handleSaveButton}
          >
            {t('marginTool.buttons.save')}
            {(isSavingScenario ||
              flagVariationInProgress ||
              isLoadDataScenario ||
              isScenarioVerification ||
              isGSACalculateFetchDataInProgress ||
              isPeopleSoftCallMarginToolInProgress ||
              isCalculatedUpdateInProgress) && (
              <Box position={'absolute'} right={8} top={11}>
                <CircularProgress size={16} color="inherit" />
              </Box>
            )}
          </Button>
        </Box>
      </Box>
      <MarginToolConfirmationModal
        open={cancelModal}
        onClose={handleCancelModalClose}
        onConfirm={handleCancelModalYes}
      />
    </>
  );
};

export default MarginToolFooter;
